import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  getLists,
  getListsWithPreviews,
  getList,
  getListChildren,
  createList,
  updateList,
  deleteList,
  addBookToList,
  removeBookFromList,
  getBookLists,
  type ListWithBooks,
} from '../api/lists';
import type { ReadingList, BookCollection } from '../types/models';

export const listKeys = {
  all: ['lists'] as const,
  lists: () => [...listKeys.all, 'list'] as const,
  list: (filter?: 'mine') => [...listKeys.lists(), { filter }] as const,
  details: () => [...listKeys.all, 'detail'] as const,
  detail: (id: number) => [...listKeys.details(), id] as const,
  forBook: (bookId: number) => [...listKeys.all, 'for-book-v2', bookId] as const,
  children: (parentId: number) => [...listKeys.all, 'children', parentId] as const,
};

export function useBookLists(bookId: number) {
  return useQuery<BookCollection[]>({
    queryKey: listKeys.forBook(bookId),
    queryFn: () => getBookLists(bookId),
    enabled: bookId > 0,
  });
}

export function useLists() {
  return useQuery({
    queryKey: listKeys.list(),
    queryFn: () => getLists(),
  });
}

export function useListsWithPreviews(owner?: 'mine') {
  return useQuery({
    queryKey: [...listKeys.list(owner), 'previews'],
    queryFn: () => getListsWithPreviews(owner),
    placeholderData: keepPreviousData,
  });
}

export function useList(id: number) {
  return useQuery({
    queryKey: listKeys.detail(id),
    queryFn: () => getList(id),
    enabled: id > 0,
  });
}

export function useListChildren(parentId: number) {
  return useQuery({
    queryKey: listKeys.children(parentId),
    queryFn: () => getListChildren(parentId),
    enabled: parentId > 0,
  });
}

export function useCreateList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createList,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listKeys.all });
    },
  });
}

export function useUpdateList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ReadingList> }) =>
      updateList(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: listKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: listKeys.lists() });
    },
  });
}

export function useDeleteList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteList,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: listKeys.all });
    },
  });
}

export function useAddBookToList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ listId, bookId }: { listId: number; bookId: number }) =>
      addBookToList(listId, bookId),
    onSuccess: (_data, { listId }) => {
      queryClient.invalidateQueries({ queryKey: listKeys.detail(listId) });
      queryClient.invalidateQueries({ queryKey: listKeys.lists() });
    },
  });
}

export function useRemoveBookFromList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ listId, bookId }: { listId: number; bookId: number }) =>
      removeBookFromList(listId, bookId),
    onMutate: async ({ listId, bookId }) => {
      await queryClient.cancelQueries({ queryKey: listKeys.detail(listId) });
      const previousList = queryClient.getQueryData<ListWithBooks>(
        listKeys.detail(listId)
      );

      if (previousList) {
        queryClient.setQueryData<ListWithBooks>(listKeys.detail(listId), {
          ...previousList,
          books: previousList.books.filter((book) => book.id !== bookId),
        });
      }

      return { previousList };
    },
    onError: (_err, { listId }, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(listKeys.detail(listId), context.previousList);
      }
    },
    onSettled: (_data, _error, { listId }) => {
      queryClient.invalidateQueries({ queryKey: listKeys.detail(listId) });
      queryClient.invalidateQueries({ queryKey: listKeys.lists() });
    },
  });
}
